import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  genAniSelector,
  generateBackroundColorEffect,
  horizontalSlideGenerator,
  wrapWithAnimation
} from '../common/AnimationCommon';
import Blanket from '../common/Blanket';
import OpenLocalDocumentContents from './OpenLocalDocumentContents';
import FileMenuInfo from './FileMenuInfo';
import backIcon from '../../img/filemenu_back.png';
import backIcon2X from '../../img/filemenu_back@2x.png';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { openDialog } from '../../store/modules/dialog';
import {
  DIALOG_TYPE,
  DOCUMENT_TYPE,
  DOC_FORMAT_CONFIG_VALUES,
  EDIT_FROM,
  MAC_OS_NAME,
  PO_EDIT_MODE,
  PRODUCT_TYPE,
  URLS
} from '../../define/global';
import { useAppDataSliceSelector } from '../../store/modules/appData';
import NewDocumentsContents from './NewDocumentsContents';
import { hideFileMenu } from '../../store/modules/popup';
import useDelayHiding from '../../hooks/delayHidingHook';
import { setMessage } from '../../store/modules/extensionHandle';
import { MESSAGE_TYPE } from '../../define/extension/message';
import ga from '../../utils/gaConnect';
import { usePOWebofficeSDKState } from '../../context/poWebOfficeSDKContext';

import { startPrint } from '../../utils/printer';
import { useDocInfoSliceSelector } from '../../store/modules/docInfo';
import { useGenNotificationPromise } from '../notification/Notification';
import { useDoFlowByDownload } from '../../utils/FileControlUtil';
import { uploadItt, useUploadItt } from '../../utils/util';
import { isProducType_STANDALONE } from '../../utils/producTypeUtils';
import { useNaverWorksDataSliceSelector } from '../../store/modules/naverWorksData';

const OpenAnimation = horizontalSlideGenerator('-100%', '0px');
const CloseAnimation = horizontalSlideGenerator('0px', '-100%');
const ANIMATION_DURATION = 300;
const LIST_ITEM_PADDING_LEFT = 30;

const FileMenuBody = wrapWithAnimation(
  styled.div`
    position: absolute;
    left: 0;
    top: 0;
    width: fit-content;
    height: 100%;
    background: white;
    display: flex;
    box-sizing: border-box;

    border-right: 1px solid #e0e0e0;
  `,
  {
    showAni: OpenAnimation,
    hideAni: CloseAnimation,
    duration: ANIMATION_DURATION
  }
);

const FileMenuContens = styled.div`
  width: 504px;
  height: 100%;
`;

const BackIcon = styled.img`
  width: 32px;
  height: 32px;
  margin: 20px 0 28px 14px;
  &:hover {
    cursor: pointer;
  }
`;

const MenuListContainer = styled.div`
  width: 180px;
  height: 100%;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
`;

const MenuListItem = styled.div`
  width: 100%;
  height: 44px;
  background: white;
  border: none;
  font-size: 13px;
  font-weight: 400;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #46494c;
  display: flex;
  align-items: center;
  padding-left: ${LIST_ITEM_PADDING_LEFT}px;
  box-sizing: border-box;

  &:hover {
    background: #f7f7f7;
    cursor: pointer;
  }
  &:active {
    background: #f0f0f0;
    cursor: pointer;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      background: #f0f0f0;
      &:hover {
        background: #f0f0f0;
      }
    `}
`;

const MenuListItemAlignBottom = styled(MenuListItem)`
  margin-top: auto;
  margin-bottom: 20px;

  ${({ isB2C }) =>
    isB2C &&
    css`
      display: flex;
      align-items: center;
    `}
`;

const TermsPrivacyArea = styled.div`
  margin-top: auto;
  margin-bottom: 20px;
  padding-left: ${LIST_ITEM_PADDING_LEFT}px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TermsPrivacyItem = styled.div`
  font-size: 11px;
  text-align: center;
  color: #2f3133;
  cursor: pointer;
`;

const MENU_TAB_LIST = {
  NEW_DOCUMENT_TAB: 'NEW_DOCUMENT_TAB',
  OPEN_DOCUMENT_TAB: 'OPEN_DOCUMENT_TAB',
  DOWNLOAD_DOCUMENT_TAB: 'DOWNLOAD_DOCUMENT_TAB'
};

function FileMenuMain({ isShow }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const appData = useAppDataSliceSelector();
  const isMacOS = appData.clientOS === MAC_OS_NAME;
  const documentType = DOC_FORMAT_CONFIG_VALUES.sdk.documnetType;
  const docInfoSlice = useDocInfoSliceSelector();
  const poSDKContextState = usePOWebofficeSDKState();
  const notificationPromise = useGenNotificationPromise();
  const doFlowByDownload = useDoFlowByDownload();
  const uploadItt = useUploadItt();
  const naverWorksData = useNaverWorksDataSliceSelector();

  const isB2C =
    appData.productType !== PRODUCT_TYPE.WITH_WHALE_SPACE &&
    appData.productType !== PRODUCT_TYPE.WITH_URL_OPENER_B2B;

  const isNotShowNewDocTab =
    appData.productType !== PRODUCT_TYPE.WITH_WHALE_SPACE &&
    docInfoSlice.poEditMode === PO_EDIT_MODE.READ &&
    documentType === DOCUMENT_TYPE.HWP &&
    isMacOS;

  const isSupportTranslationMenu =
    process.env.REACT_APP_SUPPORT_TRANSLATION === 'true' &&
    isProducType_STANDALONE(appData.productType) &&
    documentType === DOCUMENT_TYPE.WORD;

  const defaultActive = isNotShowNewDocTab
    ? MENU_TAB_LIST.OPEN_DOCUMENT_TAB
    : MENU_TAB_LIST.NEW_DOCUMENT_TAB;
  const renderable = useDelayHiding(isShow, { duration: ANIMATION_DURATION });

  const [active, setActive] = useState('');
  useEffect(() => {
    setActive(defaultActive);
  }, [defaultActive]);

  const onDownloadFile = async (from) => {
    const isDownload = (await doFlowByDownload(from, docInfoSlice.fileName)) || false;
    if (isDownload) {
      dispatch(hideFileMenu());
    }
  };

  const onWindowOpen = (url, target) => {
    window.open(url, target, 'noopener,noreferrer');
  };

  const onPrintFile = () => {
    ga.event({ category: '파일탭', action: '프린트' });
    dispatch(hideFileMenu());

    notificationPromise({
      title: t('PrintNoti.title'),
      message: t('PrintNoti.A4PaperOptimiztionMsg'),
      btn: {
        confirm: {
          text: t('PrintNoti.confirm')
        }
      },
      showCloseBtn: false
    }).then(() => {
      poSDKContextState.poInstance.handle.exportAsImage().then((document) => {
        const polarisOfficePrintWaterMarkMsg = t('Print.polarisOfficeWebWaterMsg');
        const printingWaitingMsg = t('Print.printingWaitingMsg');
        startPrint(
          document.blobs,
          '/print/print.html',
          printingWaitingMsg,
          t('Print.cancelText'),
          () => {
            notificationPromise({
              title: t('PrintNoti.title'),
              message: t('PrintNoti.popupBlock'),
              btn: {
                confirm: {
                  text: t('PrintNoti.confirm')
                }
              },
              showCloseBtn: false
            });
          }
          /*
          {
            top: {
              msg: polarisOfficePrintWaterMarkMsg
            },
            bottom: {
              msg: polarisOfficePrintWaterMarkMsg
            }
          },
          {}
          */
        );
      });
    });
  };

  const whaleExtMode =
    appData.productType === PRODUCT_TYPE.WITH_WHALE_SPACE && appData.extensionConnectMode === true
      ? true
      : false;

  return (
    renderable && (
      <>
        <Blanket
          cssExt={genAniSelector(
            generateBackroundColorEffect('rgba(224, 224, 224, 0.0)', 'rgba(224, 224, 224, 0.5)'),
            generateBackroundColorEffect('rgba(224, 224, 224, 0.5)', 'rgba(224, 224, 224, 0.0)'),
            ANIMATION_DURATION
          )}
          isTransparent={false}
          isShow={isShow}
          onClick={() => dispatch(hideFileMenu())}
        />
        <FileMenuBody show={isShow}>
          <MenuListContainer>
            <BackIcon src={backIcon} srcSet={backIcon2X} onClick={() => dispatch(hideFileMenu())} />
            {isNotShowNewDocTab === false && (
              <MenuListItem
                isActive={active === MENU_TAB_LIST.NEW_DOCUMENT_TAB}
                onClick={() => setActive(MENU_TAB_LIST.NEW_DOCUMENT_TAB)}>
                {t('FileMenu.NewDocument')}
              </MenuListItem>
            )}
            <MenuListItem
              isActive={active === MENU_TAB_LIST.OPEN_DOCUMENT_TAB}
              onClick={() => setActive(MENU_TAB_LIST.OPEN_DOCUMENT_TAB)}>
              {t('FileMenu.OpenLocalDocument')}
            </MenuListItem>
            {appData.productType === PRODUCT_TYPE.WITH_WHALE_SPACE && naverWorksData.isConnected && (
              <MenuListItem
                onClick={() => {
                  dispatch(
                    openDialog({
                      dialogType: DIALOG_TYPE.DIALOG_UPLOAD_NAVERWORKS_FILE,
                      arg: {
                        driveType: naverWorksData.naverWorksFileInfo.driveType,
                        driveId: naverWorksData.naverWorksFileInfo.driveId,
                        folderId: naverWorksData.naverWorksFileInfo.folderId,
                        fileId: naverWorksData.naverWorksFileInfo.fileId
                      }
                    })
                  );
                  dispatch(hideFileMenu());
                }}>
                {t('Header.Upload')}
              </MenuListItem>
            )}
            {whaleExtMode === true ? (
              <>
                <MenuListItem
                  onClick={() => {
                    ga.event({ category: '문서 저장', action: '저장', label: '파일탭' });
                    dispatch(setMessage({ action: MESSAGE_TYPE.ACTION_TYPE.SAVE_FILE }));
                  }}>
                  {t('Common.Save')}
                </MenuListItem>
                <MenuListItem
                  onClick={() => {
                    ga.event({ category: '문서 저장', action: '다른이름저장', label: '파일탭' });
                    dispatch(setMessage({ action: MESSAGE_TYPE.ACTION_TYPE.SAVE_AS_FILE }));
                  }}>
                  {t('Common.SaveAs')}
                </MenuListItem>
              </>
            ) : (
              !whaleExtMode && (
                <MenuListItem onClick={() => onDownloadFile(EDIT_FROM.FROM_FILEMENU)}>
                  {t('FileMenu.DownloadDocument')}
                </MenuListItem>
              )
            )}
            {appData.viaPartner && appData.showUploadBtn && (
              <MenuListItem onClick={uploadItt}>{t('Header.Upload')}</MenuListItem>
            )}
            {appData.productType === PRODUCT_TYPE.WITH_WHALE_SPACE && (
              <>
                <MenuListItem onClick={onPrintFile}>{t('Common.Print')}</MenuListItem>
                <MenuListItem
                  onClick={() =>
                    dispatch(openDialog({ dialogType: DIALOG_TYPE.DIALOG_EXPORT_PDF }))
                  }>
                  {t('FileMenu.ExportPDF')}
                </MenuListItem>
                <MenuListItemAlignBottom
                  onClick={() => {
                    window.open(URLS.FEEDBACK.EDU.KO, '_blank', 'noopener,noreferrer');
                  }}>
                  {t('FileMenu.Feedback')}
                </MenuListItemAlignBottom>
              </>
            )}
            {isSupportTranslationMenu && (
              <MenuListItem
                onClick={() => {
                  dispatch(hideFileMenu());
                  dispatch(openDialog({ dialogType: DIALOG_TYPE.DIALOG_TRANSLATE }));
                }}>
                {t('TranslateDialog.title')}
              </MenuListItem>
            )}
            {isB2C && (
              <TermsPrivacyArea>
                <TermsPrivacyItem
                  onClick={() => window.open(URLS.TERMS, '_blank', 'noopener,noreferrer')}>
                  {t('FileMenu.Terms')}
                </TermsPrivacyItem>
                <TermsPrivacyItem
                  onClick={() => window.open(URLS.PRIVACY, '_blank', 'noopener,noreferrer')}>
                  {t('FileMenu.Privacy')}
                </TermsPrivacyItem>
              </TermsPrivacyArea>
            )}
          </MenuListContainer>
          <FileMenuContens>
            {active === MENU_TAB_LIST.OPEN_DOCUMENT_TAB && <OpenLocalDocumentContents />}
            {active === MENU_TAB_LIST.NEW_DOCUMENT_TAB && <NewDocumentsContents />}
          </FileMenuContens>
          {appData.productType !== PRODUCT_TYPE.WITH_WHALE_SPACE && <FileMenuInfo />}
        </FileMenuBody>
      </>
    )
  );
}

export default FileMenuMain;
